import useTemplates from './useTemplates';
import { create } from '@composables/useNotifications';
import useLettrExportProgress, {
    ILettrExportProgress,
} from './useLettrExportProgress';

interface ILettrExportResult {
    exported: number;
    total: number;
    ready: number;
    still_processing: number;
    failures: string[];
    skipped: string[];
    preparation_failures: string[];
}

/**
 * A bulk run can partly succeed, so the toast says what actually landed rather
 * than just that the run finished.
 *
 * It counts what is ready rather than what Lettr accepted: Lettr renders the
 * HTML and migrates the images after it answers, and only a prepared template
 * can be sent.
 */
const lettrExportMessage = (event: ILettrExportResult): string => {
    // Nothing has finished yet, which is what a run reports when Topol could
    // not wait around for Lettr. "0 of 3 ready" reads like a failure; it is
    // just too early to say.
    const nothingSettledYet =
        event.ready === 0 && event.still_processing === event.exported;

    const parts = nothingSettledYet
        ? [`${event.exported} sent to Lettr, which is still preparing them`]
        : [`${event.ready} of ${event.total} ready in Lettr`];

    if (!nothingSettledYet && event.still_processing > 0) {
        parts.push(`${event.still_processing} still being prepared by Lettr`);
    }

    if (event.failures.length > 0) {
        parts.push(
            `${event.failures.length} not exported: ${event.failures.join(', ')}`
        );
    }

    if (event.preparation_failures.length > 0) {
        parts.push(
            `${event.preparation_failures.length} failed in Lettr: ${event.preparation_failures.join(', ')}`
        );
    }

    if (event.skipped.length > 0) {
        parts.push(`${event.skipped.length} skipped: ${event.skipped.join(', ')}`);
    }

    return parts.join('. ');
};

/**
 * Only something actually going wrong is an error.
 *
 * Templates Lettr is still rendering are not a failure - the export worked and
 * Lettr is busy - so a run that is merely unfinished reads as information
 * rather than as something the user has to act on.
 */
const lettrExportTone = (
    event: ILettrExportResult
): 'success' | 'error' | 'info' => {
    if (event.failures.length > 0 || event.preparation_failures.length > 0) {
        return 'error';
    }

    return event.still_processing > 0 ? 'info' : 'success';
};

const { fetchData } = useTemplates();
const { initializeCurrentFolder } = useFolders();
const { cancelMultipleSelect } = useTemplates();
const foldersAndTemplates = useFoldersAndTemplates();

const timeout = ref(0);
const listeningForEvents = ref(false);
const { update: updateLettrProgress, finish: finishLettrProgress } =
    useLettrExportProgress();

export const useMultipleTemplateActions = (userId: number) => {
    const startLoading = () => {
        foldersAndTemplates.loading.value = true;

        timeout.value = setTimeout(async () => {
            create({
                title: 'Templates are being processed. It may take a while.',
                type: 'info',
            });
            await fetchData();
            foldersAndTemplates.loading.value = false;
        }, 5000);
    };

    const cancelLoading = async () => {
        clearTimeout(timeout.value);
        await fetchData();
        foldersAndTemplates.loading.value = false;
        cancelMultipleSelect();
    };

    const listenForEvents = () => {
        if (listeningForEvents.value === true) {
            return;
        }

        listeningForEvents.value = true;

        window.Echo.private('App.Topol.Users.User.' + userId)
            .listen(
                '.App\\Topol\\Templates\\Events\\MultipleTemplatesStatusUpdated',
                async () => {
                    handleEvent('Templates status updated', 'success');
                }
            )
            .listen(
                '.App\\Topol\\Templates\\Events\\MultipleTemplatesDeleted',
                async () => {
                    handleEvent('Templates deleted', 'success');
                }
            )
            .listen(
                '.App\\Topol\\Templates\\Events\\MultipleTemplatesMoved',
                async () => {
                    handleEvent('Templates moved', 'success');
                    await initializeCurrentFolder();
                }
            )
            .listen(
                '.App\\Topol\\Templates\\Events\\MultipleTemplatesDuplicated',
                async () => {
                    handleEvent('Templates duplicated', 'success');
                }
            )
            .listen(
                '.App\\Topol\\Templates\\Events\\MultipleTemplatesLettrExportProgress',
                async (event: ILettrExportProgress) => {
                    updateLettrProgress(event);
                }
            )
            .listen(
                '.App\\Topol\\Templates\\Events\\MultipleTemplatesExportedToLettr',
                async (event: ILettrExportResult) => {
                    finishLettrProgress();
                    handleEvent(
                        lettrExportMessage(event),
                        lettrExportTone(event)
                    );
                }
            )
            .listen(
                '.App\\Topol\\Templates\\Events\\MultipleTemplatesExceptionThrown',
                async () => {
                    handleEvent(
                        'There was an error processing the templates. Please try again later.',
                        'error'
                    );
                }
            );
    };

    const handleEvent = async (
        event: string,
        type: 'success' | 'error' | 'info'
    ) => {
        create({
            title: event,
            type: type,
        });
        foldersAndTemplates.resumeSearch();
        cancelLoading();
    };

    const stopListeningForEvents = () => {
        listeningForEvents.value = false;
    };

    return {
        startLoading,
        cancelLoading,
        listenForEvents,
        handleEvent,
        stopListeningForEvents,
    };
};
